import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SEOMeta from '@/components/base/SEOMeta';
import { articles, categories } from '@/mocks/articles';
import NotFound from '@/pages/NotFound';

export default function Article() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return <NotFound />;
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  };

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishedAt,
    publisher: {
      '@type': 'Organization',
      name: '黑料吃瓜',
      url: 'https://heiliao-chigua.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://heiliao-chigua.com/article/${article.id}`,
    },
    inLanguage: 'zh-CN',
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heiliao-chigua.com/' },
      ...(category ? [{ '@type': 'ListItem' as const, position: 2, name: category.name, item: `https://heiliao-chigua.com/category/${category.slug}` }] : []),
      { '@type': 'ListItem', position: category ? 3 : 2, name: article.title, item: `https://heiliao-chigua.com/article/${article.id}` },
    ],
  };

  return (
    <>
      <SEOMeta
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        ogType="article"
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogImage={article.image}
        schema={[articleSchema, breadcrumbSchema]}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 max-w-7xl mx-auto py-10 md:py-14">
          <div className="flex flex-col lg:flex-row gap-10">
            <div className="flex-1 max-w-3xl">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4 hover:bg-primary-200 transition-colors cursor-pointer"
                >
                  <i className={`${category.icon}`}></i>
                  {category.name}
                </Link>
              )}

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center flex-wrap gap-4 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200/60">
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line"></i>
                  {formatDate(article.publishedAt)}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-eye-line"></i>
                  {formatViews(article.views)} 阅读
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-chat-3-line"></i>
                  {article.comments} 评论
                </span>
              </div>

              <div className="rounded-2xl overflow-hidden mb-10">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-auto object-cover"
                />
              </div>

              <div className="prose-custom text-foreground-800 leading-relaxed text-base">
                {article.content.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="mb-5 text-foreground-700 leading-loose">
                    {paragraph.trim()}
                  </p>
                ))}
              </div>

              {article.tags.length > 0 && (
                <div className="mt-10 pt-8 border-t border-background-200/60">
                  <div className="flex items-center flex-wrap gap-2">
                    <span className="text-sm text-foreground-500 mr-1">{t('article.tags')}：</span>
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8 flex items-center justify-between pt-6 border-t border-background-200/60">
                <Link
                  to={category ? `/category/${category.slug}` : '/'}
                  className="flex items-center gap-2 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  {t('article.backToList')}
                </Link>
                <div className="flex items-center gap-3">
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer" aria-label="分享">
                    <i className="ri-share-forward-line"></i>
                  </button>
                  <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-500 transition-colors cursor-pointer" aria-label="收藏">
                    <i className="ri-bookmark-line"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="lg:w-80 flex-shrink-0">
              <div className="sticky top-24">
                {relatedArticles.length > 0 && (
                  <div className="bg-background-100 rounded-2xl p-6 border border-background-200/60">
                    <h3 className="font-heading text-lg font-bold text-foreground-900 mb-5">
                      {t('article.relatedArticles')}
                    </h3>
                    <div className="space-y-2">
                      {relatedArticles.map((ra) => (
                        <ArticleCard key={ra.id} {...ra} variant="compact" />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}